import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  parseContinuationPage,
  readPolyRuntimeData,
  type SearchResultItem,
  type SearchRuntimeData,
} from './searchRuntimeData';
import './SearchResults.scss';

/**
 * SearchResults: the results region for the site search page.
 *
 * Reads the per-request payload Jellyfish injects as `window.searchResults`
 * (see `searchRuntimeData.ts`) for page 1, then fetches further pages from a
 * continuation endpoint on demand. There is no visible count header and no
 * pagination footer; a load-more control replaces both.
 *
 * SSR renders the empty/prompt state: there is no `window` on the server, so
 * the reader returns `null` and a no-JS visitor sees the search input plus a
 * prompt rather than a blank region. Results arrive in a mount effect, which
 * follows the SSR-safe `clientUrl` pattern in `MegaMenu`.
 *
 * `dangerouslySetInnerHTML` is deliberately absent. Query text and result
 * titles are attacker-influenced, so title bolding splits the title into text
 * nodes around matches and every query echo renders as a text node.
 */

/** Load-more lifecycle. `exhausted` hides the control; `error` offers a retry. */
type LoadMoreState = 'idle' | 'loading-more' | 'appending' | 'exhausted' | 'error';

/**
 * @polySemanticUsage About this block: the search results region for the site
 * search page. Renders results injected server-side for the first page and
 * fetches further pages on demand. Place once, below the search input, on the
 * /search document. It is not a content list an author curates, because the
 * entries come from the search backend at request time.
 */
export interface SearchResultsProps {
  /**
   * Endpoint the load-more control fetches subsequent pages from. Jellyfish
   * mints the backend-tagged continuation token; the component treats it as
   * opaque.
   * @polyType Url
   * @polyLabel Continuation Endpoint
   * @polyDefault /search/results
   * @polyTranslatable false
   */
  continuationEndpoint?: string;
  /**
   * Query-string param name carrying the user's query on the continuation call.
   * @polyTranslatable false
   */
  queryParamName?: string;
  /**
   * Query-string param name carrying the opaque continuation token.
   * @polyTranslatable false
   */
  continuationParamName?: string;

  /**
   * Empty/prompt copy shown when a query returned nothing. Supports the
   * literal placeholder `{{ search-query }}`, replaced client-side with the
   * user's query.
   */
  emptyPrompt: string;
  /** Prompt shown on a bare /search with no query at all. */
  noQueryPrompt?: string;
  /** "Did you mean" prefix rendered before the corrected-query link. */
  misspelledLabel?: string;
  /** Load-more button label. */
  loadMoreLabel?: string;
  /** Label shown while a load-more fetch is in flight. */
  loadingLabel?: string;
  /** Error message shown if a load-more fetch fails. */
  errorLabel?: string;
  /** Retry button label for the error state. */
  retryLabel?: string;

  /**
   * Screen-reader-only result-count templates. NOT rendered visibly, since the
   * count header is removed. Used only for the aria-live announcement.
   * `{{ total }}` is substituted.
   */
  resultsSingular?: string;
  /** Plural form of the screen-reader count template. */
  resultsPlural?: string;
  /**
   * Accessible name for the results region landmark, so screen-reader users
   * can jump straight to it. Translatable so it is announced in the page
   * language.
   * @polyLabel Screen Reader Label
   * @polyHelp Name screen readers announce for the results region. Translated with the page.
   * @polyDefault Search results
   */
  ariaLabel?: string;

  /**
   * Storybook / testing only: seed results directly, bypassing
   * `window.searchResults`.
   * @polyIgnore
   */
  initialData?: SearchRuntimeData;
}

const QUERY_TOKEN = '{{ search-query }}';
const ORIGINAL_QUERY_TOKEN = '{{ original_query }}';
const TOTAL_TOKEN = '{{ total }}';

/** Escapes a user-supplied string for literal use inside a RegExp. */
function escapeForRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Splits `text` on each token in turn, interleaving each value as its own
 * text node.
 *
 * Substituted values become React nodes immediately and are never re-scanned,
 * so a value carrying a token literal cannot trigger a second substitution.
 * That is defense in depth, not currently observable behavior: both existing
 * tokens resolve to the same value, so a naive chained `.replace()` produces
 * identical output. It starts to matter the moment a token resolves to
 * something different, which is why the guarantee lives in the structure
 * rather than in a test that would pass either way.
 *
 * When a token is absent the copy renders unchanged. That is the graceful
 * degradation path for a locale variant where translation dropped the
 * placeholder: no `{{...}}` artifact is ever shown for a SUPPORTED token and
 * nothing throws.
 */
function renderWithTokens(
  text: string,
  substitutions: ReadonlyArray<readonly [string, string]>
): React.ReactNode[] {
  let parts: Array<string | React.ReactNode> = [text];
  for (const [token, value] of substitutions) {
    const next: Array<string | React.ReactNode> = [];
    for (const part of parts) {
      if (typeof part !== 'string') {
        next.push(part);
        continue;
      }
      const pieces = part.split(token);
      pieces.forEach((piece, index) => {
        if (piece) {
          next.push(piece);
        }
        if (index < pieces.length - 1) {
          // Wrapping now is what marks the value as done: only bare strings
          // are re-split by later tokens.
          next.push(<React.Fragment>{value}</React.Fragment>);
        }
      });
    }
    parts = next;
  }
  return parts.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>);
}

/**
 * Bolds every case-insensitive occurrence of `query` inside `text`.
 *
 * Returns Preact/React elements built from split text nodes, never an HTML
 * string, so a title or query containing markup renders inert.
 */
function boldQueryMatches(text: string, query: string): React.ReactNode[] {
  const trimmed = query.trim();
  if (!trimmed) {
    return [<React.Fragment key="t0">{text}</React.Fragment>];
  }
  const pattern = new RegExp(`(${escapeForRegExp(trimmed)})`, 'gi');
  return text
    .split(pattern)
    .filter(segment => segment !== '')
    .map((segment, index) =>
      segment.toLowerCase() === trimmed.toLowerCase() ? (
        <strong key={index} className="search-results__match">
          {segment}
        </strong>
      ) : (
        <React.Fragment key={index}>{segment}</React.Fragment>
      )
    );
}

/** Inert target for an href whose scheme is not safe to navigate to. */
const BLOCKED_HREF = '#';

/**
 * Returns the href only when it uses a scheme that is safe to navigate to.
 *
 * React does not sanitize `href` (and production ships Preact, which does not
 * either), so a result carrying a `javascript:` or `data:` URL would execute
 * on click. The reader upstream checks that these fields are strings, not that
 * they are safe, and the whole payload is attacker-influenced. Relative paths
 * and fragments carry no scheme and pass through unchanged.
 *
 * ASCII control characters are stripped BEFORE scheme detection: the URL
 * parser removes tab, LF and CR from input before parsing, so `java\tscript:`
 * navigates as `javascript:`. Detecting the scheme on the raw string is the
 * classic obfuscation bypass. The cleaned value is also what gets returned,
 * since control characters have no legitimate place in an href.
 *
 * This deliberately does not police protocol-relative (`//host`) URLs: those
 * navigate rather than execute, and treating them as hostile would break
 * legitimate absolute links.
 */
function safeHref(href: string): string {
  const cleaned = href.replace(/[\u0000-\u001f\u007f]/g, '').trim();
  const scheme = /^([a-z][a-z0-9+.-]*):/i.exec(cleaned);
  if (!scheme) {
    return cleaned;
  }
  if (/^https?$/i.test(scheme[1])) {
    return cleaned;
  }
  // Dev diagnostic only: this runs during render, once per blocked link per
  // re-render, so it is env-gated like LeadCapture's render-adjacent warn.
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn('[SearchResults] Blocked a result link with an unsafe scheme:', scheme[1]);
  }
  return BLOCKED_HREF;
}

/**
 * Primary click-through target, preserving click tracking when present.
 *
 * Falsy rather than nullish fallback: an empty-string `redirection-url` must
 * fall back to `url`, not render an empty href that navigates to the current
 * page. Same reasoning as the continuation cursor in the reader.
 */
function primaryHref(result: SearchResultItem): string {
  return safeHref(result['redirection-url'] || result.url);
}

/**
 * Whether a payload has anywhere left to page.
 *
 * A live token alone is not enough. The reader derives `continuation` from the
 * payload independently of `results`, so a page can carry a token with nothing
 * renderable: an empty list, or every entry dropped by the reader's guards.
 * Paging from there adds no row, so it is exhaustion. The continuation-fetch
 * path already reaches the same conclusion for an empty page.
 *
 * This is the single home for that rule. The load-more state and the control's
 * render gate both derive from it, so they cannot drift apart: `exhausted`
 * means exhausted, and a rendered control always has somewhere to go.
 *
 * Falsy rather than nullish on the token, consistent with the reader's
 * `coerceString(...) || null` normalization.
 *
 * Exported for direct testing. While the control is hidden the load-more state
 * has no observable projection, so this predicate is the only place the rule
 * can be pinned; a DOM assertion would cover the render gate alone.
 */
export function hasPageableRemainder(data: SearchRuntimeData | null | undefined): boolean {
  return Boolean(data?.continuation) && (data?.results ?? []).length > 0;
}

export const SearchResults: React.FC<SearchResultsProps> = ({
  continuationEndpoint = '/search/results',
  queryParamName = 'search',
  continuationParamName = 'continuation',
  emptyPrompt,
  noQueryPrompt,
  misspelledLabel,
  loadMoreLabel = 'Show more results',
  loadingLabel = 'Loading…',
  errorLabel = "Couldn't load more results.",
  retryLabel = 'Try again',
  resultsSingular,
  resultsPlural,
  ariaLabel,
  initialData,
}) => {
  const [data, setData] = useState<SearchRuntimeData | null>(initialData ?? null);
  const [loadState, setLoadState] = useState<LoadMoreState>(
    hasPageableRemainder(initialData) ? 'idle' : 'exhausted'
  );
  /** Index of the first result appended by the most recent load-more. */
  const [appendedFrom, setAppendedFrom] = useState<number | null>(null);

  /**
   * Synchronous double-click guard. `loadState` alone is not enough: two
   * clicks in the same tick both read the pre-update state.
   */
  const inFlightRef = useRef(false);
  const appendedRef = useRef<HTMLLIElement | null>(null);

  // SSR-safe read of the per-request payload. There is no `window` on the
  // server, so the first render is always the prompt state; results replace it
  // after mount. Same shape as MegaMenu's `clientUrl` effect.
  useEffect(() => {
    if (initialData) {
      return;
    }
    const runtime = readPolyRuntimeData();
    if (runtime) {
      setData(runtime);
      setLoadState(hasPageableRemainder(runtime) ? 'idle' : 'exhausted');
    }
  }, [initialData]);

  const results = data?.results ?? [];
  const continuation = data?.continuation ?? null;
  const originalQuery = data?.original_query ?? '';
  const correctedQuery = data?.corrected_query ?? '';

  /**
   * The query actually searched. When the backend auto-corrected, this is the
   * correction, so it is also what the continuation call must page on, never
   * the raw original.
   */
  const effectiveQuery = correctedQuery || originalQuery;

  const handleLoadMore = useCallback(async () => {
    // `aria-disabled` does not block clicks the way `disabled` does, so the
    // busy window is guarded here: the ref covers the in-flight fetch and the
    // state check covers the brief post-fetch `appending` render before the
    // focus effect settles the state.
    if (inFlightRef.current || loadState === 'appending' || !continuation) {
      return;
    }
    inFlightRef.current = true;
    setLoadState('loading-more');

    const url =
      `${continuationEndpoint}?${queryParamName}=${encodeURIComponent(effectiveQuery)}` +
      `&${continuationParamName}=${encodeURIComponent(continuation)}`;

    try {
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Continuation failed: HTTP ${response.status}`);
      }
      const page = parseContinuationPage(await response.json());
      if (!page) {
        throw new Error('Continuation response was not a recognized page');
      }
      const appended = page.results;
      // A page with no results but a live token would leave the user clicking a
      // control that changes nothing: no new rows, no count change, and no
      // focus target for the append effect. Treat it as exhaustion so the
      // control disappears instead of inviting another no-op click.
      if (appended.length > 0) {
        setAppendedFrom(results.length);
      }
      setData(current => ({
        results: [...(current?.results ?? []), ...appended],
        original_query: current?.original_query,
        corrected_query: current?.corrected_query,
        continuation: appended.length === 0 ? null : page.continuation,
      }));
      setLoadState(appended.length > 0 ? 'appending' : 'exhausted');
    } catch (error) {
      // The continuation endpoint and its response shape are owned by
      // Jellyfish, so a drift there surfaces here. Keep the reason in the
      // console: the user-facing state says only "try again".
      // eslint-disable-next-line no-console
      console.warn('[SearchResults] Continuation request failed:', error);
      setLoadState('error');
    } finally {
      inFlightRef.current = false;
    }
  }, [
    continuation,
    continuationEndpoint,
    continuationParamName,
    effectiveQuery,
    loadState,
    queryParamName,
    results.length,
  ]);

  // Move focus to the first appended result so a keyboard user resumes where
  // the new content starts instead of being dropped at the page bottom.
  useEffect(() => {
    if (loadState !== 'appending') {
      return;
    }
    appendedRef.current?.focus();
    setLoadState(continuation ? 'idle' : 'exhausted');
  }, [loadState, continuation]);

  const countAnnouncement = useMemo(() => {
    if (results.length === 0) {
      return '';
    }
    const template = results.length === 1 ? resultsSingular : resultsPlural;
    return template ? template.split(TOTAL_TOKEN).join(String(results.length)) : '';
  }, [results.length, resultsSingular, resultsPlural]);

  // The prompt shows what the USER typed. When the backend auto-corrected, the
  // correction renders in its own did-you-mean line, so substituting the
  // corrected term here would show it twice and misquote the user.
  const promptCopy = originalQuery ? emptyPrompt : (noQueryPrompt ?? emptyPrompt);

  // Both query tokens resolve to the typed query: `{{ search-query }}` is the
  // documented primary and `{{ original_query }}` the design's legacy-parity
  // alias, kept so a locale variant authored with either never renders a
  // literal `{{...}}` artifact. The correction is never a token; it renders
  // only in the did-you-mean line.
  const queryTokens: ReadonlyArray<readonly [string, string]> = [
    [QUERY_TOKEN, originalQuery],
    [ORIGINAL_QUERY_TOKEN, originalQuery],
  ];

  // One button covers load-more, in-flight, and retry. Swapping in a separate
  // retry element would unmount the button the user just activated and drop
  // keyboard focus to the document body mid-interaction.
  //
  // Busy is conveyed with `aria-disabled`, NOT `disabled`: browsers move focus
  // off a focused element the moment it is disabled, which drops the keyboard
  // user to the document body on every click. jsdom does not model that, so
  // only a real browser catches it. Clicks while busy are ignored by
  // handleLoadMore's guards.
  const loadMoreBusy = loadState === 'loading-more' || loadState === 'appending';
  let loadMoreButtonLabel = loadMoreLabel;
  if (loadState === 'error') {
    loadMoreButtonLabel = retryLabel;
  } else if (loadState === 'loading-more') {
    loadMoreButtonLabel = loadingLabel;
  }

  return (
    <section className="search-results" role="region" aria-label={ariaLabel || 'Search results'}>
      <div className="search-results__inner">
        <div className="poly-visually-hidden" role="status" aria-live="polite">
          {countAnnouncement}
        </div>

        {misspelledLabel && correctedQuery && (
          <p className="search-results__misspelled">
            {renderWithTokens(misspelledLabel, queryTokens)}{' '}
            <a
              className="search-results__misspelled-link"
              href={`?${queryParamName}=${encodeURIComponent(correctedQuery)}`}
            >
              {correctedQuery}
            </a>
          </p>
        )}

        {results.length === 0 ? (
          <p className="search-results__prompt">
            {renderWithTokens(promptCopy, queryTokens)}
          </p>
        ) : (
          <ol className="search-results__list">
            {results.map((result, index) => {
              const anchors = result['anchor-links'];
              return (
                <li
                  key={`${result.url}-${index}`}
                  className="search-results__item"
                  tabIndex={-1}
                  ref={index === appendedFrom ? appendedRef : undefined}
                >
                  <h3 className="search-results__item-title">
                    <a className="search-results__item-link" href={primaryHref(result)}>
                      {boldQueryMatches(result.title, effectiveQuery)}
                    </a>
                  </h3>
                  {result.description && (
                    <p className="search-results__item-description">{result.description}</p>
                  )}
                  {anchors && anchors.length > 0 && (
                    <ul className="search-results__anchors">
                      {anchors.map(anchor => (
                        <li key={anchor.link} className="search-results__anchor">
                          <a
                            className="search-results__anchor-link"
                            href={safeHref(`${result.url}#${anchor.link}`)}
                          >
                            {anchor.displayName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              );
            })}
          </ol>
        )}

        {loadState === 'error' && (
          <p className="search-results__error-message" role="alert">
            {errorLabel}
          </p>
        )}

        {/* See `hasPageableRemainder` for why a live token alone is not enough. */}
        {hasPageableRemainder(data) && (
          // Raw <button> rather than the library's CtaButton on purpose.
          // CtaButton is the brand marketing CTA: its variants and sizes are
          // Figma-pinned and applied through Cloudscape's Style API, so a new
          // utility size would have to be added to CTA_SIZES, SIZE_PADDING and
          // the SCSS specificity overrides just to render one pagination
          // control. This is utility chrome, not a marketing CTA.
          <button
            type="button"
            className="search-results__load-more"
            onClick={handleLoadMore}
            aria-disabled={loadMoreBusy}
            aria-busy={loadState === 'loading-more'}
          >
            {loadMoreButtonLabel}
          </button>
        )}
      </div>
    </section>
  );
};
